/* FREDDY Pantroom — product page behaviour (00-architecture §E.4; concept shop.js `if(pdp)` + Shopify cart).
   Reads <script type="application/json" data-freddy-product> (sections/freddy-main-product.liquid).
   Delegated listeners on document: they survive theme-editor section re-renders. Uses FREDDY.cart.add (freddy-cart.js)
   and FREDDY.show / FREDDY.close (freddy-theme.js) when present, with small local fallbacks.
   Zoom viewer (dialog#zoom-dialog, sections/freddy-product-dialogs.liquid): opens at the gallery's current image;
   previous / next buttons, Left / Right keys and (unmagnified) swipes switch images; click / tap / Enter / Space
   magnify, drag pans, pinch zooms; switching resets magnification and pan; focus returns to the opener on close. */
(() => {
  'use strict';

  const F = (window.FREDDY = window.FREDDY || {});
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const decoder = document.createElement('textarea');

  /* Texts may arrive HTML-escaped (Liquid `t`) or contain money-format markup: strip tags, decode entities. */
  const text = (value) => {
    if (value === null || value === undefined) return '';
    const raw = String(value).replace(/<[^>]*>/g, '');
    if (raw.indexOf('&') === -1) return raw;
    decoder.innerHTML = raw;
    return decoder.value;
  };

  const state = { el: null, data: null, selected: null, busy: false };

  function load() {
    const el = document.querySelector('script[data-freddy-product]');
    if (el === state.el) return state.data;
    state.el = el;
    state.data = null;
    state.selected = null;
    state.busy = false;
    if (!el) return null;
    try {
      state.data = JSON.parse(el.textContent);
    } catch (_) {
      state.data = null;
    }
    if (state.data) {
      state.data.strings = state.data.strings || {};
      state.data.variants = Array.isArray(state.data.variants) ? state.data.variants : [];
      if (state.data.selected_id) state.selected = variantById(state.data.selected_id) || null;
    }
    return state.data;
  }

  function variantById(id) {
    const data = state.data;
    if (!data) return null;
    const wanted = Number(id);
    return data.variants.find((v) => Number(v.id) === wanted) || null;
  }

  /* Section string (resolved setting / locale) → FREDDY.strings fallback (layout) → ''. */
  function str(key, globalKey) {
    const own = state.data && state.data.strings ? state.data.strings[key] : '';
    if (own) return text(own);
    const global = F.strings && globalKey ? F.strings[globalKey] : '';
    return global ? text(global) : '';
  }

  function maxQuantity() {
    const max = Number(state.data && state.data.max);
    return Number.isInteger(max) && max > 0 ? max : 10;
  }

  function show(target) {
    const dialog = typeof target === 'string' ? $(target) : target;
    if (!dialog) return;
    if (typeof F.show === 'function') {
      F.show(dialog);
      return;
    }
    if (!dialog.open && typeof dialog.showModal === 'function') dialog.showModal();
  }

  function close(target) {
    const dialog = typeof target === 'string' ? $(target) : target;
    if (!dialog || !dialog.open) return;
    if (typeof F.close === 'function') F.close(dialog);
    else dialog.close();
  }

  /* ---- gallery ---------------------------------------------------------------------------------------- */
  /* The zoom viewer reads the selected image when it opens (galleryIndex), so only the main image is swapped here.
     Gallery image size (snippets/freddy-pdp-gallery.liquid): thumbs may carry data-focal (off-centre focal point,
     read by the CSS as --freddy-focal-point) and, for 'Natural', data-width / data-height (the box keeps the new
     image's proportions); the main image follows them. The theme-default gallery has neither, so nothing changes there. */
  function selectThumb(button) {
    $$('.product-thumbs button').forEach((thumb) => thumb.setAttribute('aria-pressed', String(thumb === button)));
    const src = button.dataset.image;
    if (!src) return;
    const srcset = button.dataset.srcset || '';
    const img = $('#main-product-image');
    if (!img) return;
    const { width, height, focal } = button.dataset;
    if (width && height) {
      img.setAttribute('width', width);
      img.setAttribute('height', height);
    } else {
      img.removeAttribute('width');
      img.removeAttribute('height');
    }
    if (focal) img.style.setProperty('--freddy-focal-point', focal);
    else if (img.style.getPropertyValue('--freddy-focal-point')) img.style.removeProperty('--freddy-focal-point');
    if (srcset) img.srcset = srcset;
    else img.removeAttribute('srcset');
    img.src = src;
  }

  /* Gallery arrows / swipe on the main photo: select the previous / next thumb (wrapping) and keep it in view. */
  function stepThumb(direction) {
    const thumbs = $$('.product-thumbs button');
    if (thumbs.length < 2) return;
    const pressed = thumbs.findIndex((thumb) => thumb.getAttribute('aria-pressed') === 'true');
    const next = thumbs[(Math.max(pressed, 0) + direction + thumbs.length) % thumbs.length];
    selectThumb(next);
    const strip = next.parentElement;
    if (strip && strip.scrollWidth > strip.clientWidth) {
      const left = next.offsetLeft - strip.offsetLeft;
      if (left < strip.scrollLeft || left + next.offsetWidth > strip.scrollLeft + strip.clientWidth) {
        strip.scrollTo({ left: left - (strip.clientWidth - next.offsetWidth) / 2, behavior: 'smooth' });
      }
    }
  }

  let swipeStart = null;
  let swipeUntil = 0;
  document.addEventListener('touchstart', (event) => {
    const photo = event.target instanceof Element ? event.target.closest('.main-photo') : null;
    swipeStart = photo && event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
  }, { passive: true });
  document.addEventListener('touchend', (event) => {
    if (!swipeStart || !event.changedTouches.length) return;
    const dx = event.changedTouches[0].clientX - swipeStart.x;
    const dy = event.changedTouches[0].clientY - swipeStart.y;
    swipeStart = null;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    swipeUntil = Date.now() + 400; // a swipe is not a tap: skip the zoom click that may follow
    stepThumb(dx < 0 ? 1 : -1);
  }, { passive: true });

  /* ---- zoom viewer ------------------------------------------------------------------------------------ */
  /* dialog#zoom-dialog > div[data-zoom-stage] > img (product.images = thumbs order; the current one is the only img
     without [hidden]) + button[data-zoom-prev] / [data-zoom-next] + div[data-zoom-count]. The current image is read
     from the DOM on every event and `view` only holds the magnification of that img, so a theme-editor re-render of
     either section needs no re-init (a replaced img simply starts unmagnified). Magnify = translate + scale on the
     img (transform-origin 0 0), clamped so the visible picture always covers the stage (or is centred on an axis
     where it is smaller than the stage). */
  const ZOOM_SCALE = 2.5;
  const ZOOM_MAX = 4;
  const SWIPE_MIN = 50;
  const view = { img: null, scale: 1, x: 0, y: 0, gesture: null, pointers: new Map(), trigger: null };

  const zoomDialog = () => document.getElementById('zoom-dialog');
  const zoomSlides = (dialog) => (dialog ? $$('[data-zoom-stage] > img', dialog) : []);
  const zoomStage = (img) => (img && img.isConnected ? img.closest('[data-zoom-stage]') : null);
  const zoomCurrent = (slides) => Math.max(0, slides.findIndex((img) => !img.hidden));
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  /* Same file in any rendition: URL path without ?width= / ?v= */
  function imageKey(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.href).pathname;
    } catch (_) {
      return String(url).split('?')[0];
    }
  }

  /* The image the gallery shows now: the pressed thumb when the main image still matches it, else the slide with the
     main image's file (e.g. swapped by other code), else the pressed thumb, else the first image. */
  function galleryIndex(slides) {
    const thumbs = $$('.product-thumbs button');
    const pressed = thumbs.findIndex((thumb) => thumb.getAttribute('aria-pressed') === 'true');
    const main = $('#main-product-image');
    const mainKey = main ? imageKey(main.getAttribute('src')) : '';
    if (pressed >= 0 && pressed < slides.length && (!mainKey || imageKey(thumbs[pressed].dataset.image) === mainKey)) return pressed;
    if (mainKey) {
      const match = slides.findIndex((img) => imageKey(img.getAttribute('src')) === mainKey);
      if (match >= 0) return match;
    }
    return pressed >= 0 && pressed < slides.length ? pressed : 0;
  }

  /* Box of the uncropped picture (object-fit: contain, centred) inside the stage-sized img, in stage pixels. */
  function contentBox(stage, img) {
    const w = stage.clientWidth;
    const h = stage.clientHeight;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh || !w || !h) return { w, h, x: 0, y: 0, cw: w, ch: h };
    const ratio = Math.min(w / nw, h / nh);
    const cw = nw * ratio;
    const ch = nh * ratio;
    return { w, h, x: (w - cw) / 2, y: (h - ch) / 2, cw, ch };
  }

  function clampAxis(offset, scale, size, start, length) {
    const scaled = length * scale;
    if (scaled <= size) return (size - scaled) / 2 - start * scale;
    return clamp(offset, size - (start + length) * scale, -start * scale);
  }

  function clampView(stage) {
    const box = contentBox(stage, view.img);
    view.x = clampAxis(view.x, view.scale, box.w, box.x, box.cw);
    view.y = clampAxis(view.y, view.scale, box.h, box.y, box.ch);
  }

  function applyView(stage) {
    const img = view.img;
    if (!img || !stage) return;
    const zoomed = view.scale > 1;
    img.style.transform = zoomed ? 'translate(' + view.x + 'px,' + view.y + 'px) scale(' + view.scale + ')' : '';
    stage.classList.toggle('is-zoomed', zoomed);
    stage.setAttribute('aria-pressed', String(zoomed));
  }

  function resetView(img) {
    if (view.img && view.img !== img) view.img.style.transform = '';
    view.img = img || null;
    view.scale = 1;
    view.x = 0;
    view.y = 0;
    view.gesture = null;
    view.pointers.clear();
    if (img) img.style.transform = '';
  }

  /* `view` follows the visible slide: a slide switch or a re-rendered dialog starts unmagnified. */
  function syncView(dialog) {
    const slides = zoomSlides(dialog);
    const img = slides.length ? slides[zoomCurrent(slides)] : null;
    if (view.img !== img) resetView(img);
    return img;
  }

  /* Magnified: ask for a srcset candidate as wide as the magnified picture (never back down for this img). */
  function sharpen(stage, img) {
    if (!img.getAttribute('srcset')) return;
    const want = Math.ceil(contentBox(stage, img).cw * view.scale);
    if (want > Number(img.dataset.zoomSizes || 0) && want > stage.clientWidth) {
      img.dataset.zoomSizes = String(want);
      img.sizes = want + 'px';
    }
  }

  /* Scale to `scale` keeping the picture point under (clientX, clientY) in place. */
  function zoomAround(stage, scale, clientX, clientY, from) {
    const rect = stage.getBoundingClientRect();
    const base = from || view;
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    const u = (px - base.x) / base.scale;
    const v = (py - base.y) / base.scale;
    view.scale = scale;
    view.x = px - u * scale;
    view.y = py - v * scale;
    clampView(stage);
  }

  function toggleZoom(stage, clientX, clientY) {
    const img = syncView(stage.closest('dialog'));
    if (!img) return;
    stage.classList.remove('is-dragging');
    if (view.scale > 1) {
      view.scale = 1;
      view.x = 0;
      view.y = 0;
    } else {
      const rect = stage.getBoundingClientRect();
      const x = typeof clientX === 'number' ? clientX : rect.left + rect.width / 2;
      const y = typeof clientY === 'number' ? clientY : rect.top + rect.height / 2;
      zoomAround(stage, ZOOM_SCALE, x, y);
      sharpen(stage, img);
    }
    applyView(stage);
  }

  function updateCount(dialog, index, count) {
    const box = $('[data-zoom-count]', dialog);
    if (!box) return;
    const visual = box.children[0];
    const spoken = box.children[1];
    if (visual) visual.textContent = index + ' / ' + count;
    if (spoken) spoken.textContent = (box.dataset.position || index + ' / ' + count).split('[index]').join(index).split('[total]').join(count);
  }

  /* Show slide `index` (wraps around), unmagnified; preload its neighbours; nav hidden with fewer than 2 images. */
  function showSlide(dialog, index) {
    const slides = zoomSlides(dialog);
    const count = slides.length;
    if (!count) return;
    const current = ((index % count) + count) % count;
    slides.forEach((img, i) => {
      img.hidden = i !== current;
      img.style.transform = '';
    });
    const stage = $('[data-zoom-stage]', dialog);
    if (stage) {
      stage.classList.remove('is-zoomed', 'is-dragging');
      stage.setAttribute('aria-pressed', 'false');
    }
    resetView(slides[current]);
    [current, current + 1, current - 1].forEach((i) => {
      const img = slides[(i + count) % count];
      if (img.loading === 'lazy') img.loading = 'eager';
    });
    $$('[data-zoom-prev], [data-zoom-next], [data-zoom-count]', dialog).forEach((el) => {
      el.hidden = count < 2;
    });
    updateCount(dialog, current + 1, count);
  }

  function stepSlide(dialog, delta) {
    const slides = zoomSlides(dialog);
    if (slides.length > 1) showSlide(dialog, zoomCurrent(slides) + delta);
  }

  function openZoom(trigger) {
    const dialog = zoomDialog();
    const slides = zoomSlides(dialog);
    if (!slides.length) return;
    view.trigger = trigger || null;
    showSlide(dialog, galleryIndex(slides));
    show(dialog);
  }

  /* Hovering / focusing the main photo starts loading the image the viewer will open with. */
  function preloadZoom() {
    const slides = zoomSlides(zoomDialog());
    const img = slides[galleryIndex(slides)];
    if (img && img.loading === 'lazy') img.loading = 'eager';
  }

  const pointerList = () => Array.from(view.pointers.values());
  const midpoint = (points) => ({ x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 });
  const distance = (points) => Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) || 1;

  function startPinch(stage) {
    const points = pointerList();
    view.gesture = { type: 'pinch', d0: distance(points), mid: midpoint(points), from: { scale: view.scale, x: view.x, y: view.y } };
    stage.classList.add('is-dragging');
  }

  function startDrag(id, point, moved) {
    view.gesture = { type: 'drag', id, sx: point.x, sy: point.y, ox: view.x, oy: view.y, moved, pinched: moved };
  }

  function onPointerDown(event) {
    const target = event.target instanceof Element ? event.target : null;
    const stage = target ? target.closest('[data-zoom-stage]') : null;
    if (!stage || (event.pointerType === 'mouse' && event.button !== 0)) return;
    if (!syncView(stage.closest('dialog'))) return;
    view.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    try {
      stage.setPointerCapture(event.pointerId);
    } catch (_) {
      /* pointer already released */
    }
    if (view.pointers.size === 2) startPinch(stage);
    else if (view.pointers.size === 1) startDrag(event.pointerId, { x: event.clientX, y: event.clientY }, false);
  }

  function onPointerMove(event) {
    const gesture = view.gesture;
    if (!gesture || !view.pointers.has(event.pointerId)) return;
    const stage = zoomStage(view.img);
    if (!stage) return;
    view.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (gesture.type === 'pinch') {
      if (view.pointers.size < 2) return;
      const points = pointerList();
      const scale = clamp((gesture.from.scale * distance(points)) / gesture.d0, 1, ZOOM_MAX);
      const mid = midpoint(points);
      const rect = stage.getBoundingClientRect();
      /* the picture point under the starting midpoint follows the current midpoint */
      const u = (gesture.mid.x - rect.left - gesture.from.x) / gesture.from.scale;
      const v = (gesture.mid.y - rect.top - gesture.from.y) / gesture.from.scale;
      view.scale = scale;
      view.x = mid.x - rect.left - u * scale;
      view.y = mid.y - rect.top - v * scale;
      clampView(stage);
      applyView(stage);
      return;
    }
    if (event.pointerId !== gesture.id) return;
    const dx = event.clientX - gesture.sx;
    const dy = event.clientY - gesture.sy;
    if (!gesture.moved) {
      if (Math.hypot(dx, dy) < (event.pointerType === 'mouse' ? 4 : 8)) return;
      gesture.moved = true;
      stage.classList.add('is-dragging');
    }
    if (view.scale > 1) {
      view.x = gesture.ox + dx;
      view.y = gesture.oy + dy;
      clampView(stage);
      applyView(stage);
    } else if (event.pointerType !== 'mouse' && !gesture.pinched && zoomSlides(stage.closest('dialog')).length > 1) {
      /* unmagnified swipe: the picture follows the finger a little */
      view.img.style.transform = 'translate(' + Math.round(dx * 0.4) + 'px,0)';
    }
  }

  /* End of a pinch (fingers lift one after the other, so this runs when the first one lifts): an almost-1x
     result snaps back to unmagnified (swipes navigate again), otherwise ask for a sharper file. */
  function settlePinch(stage) {
    if (view.scale < 1.05) {
      view.scale = 1;
      view.x = 0;
      view.y = 0;
    } else {
      sharpen(stage, view.img);
    }
    applyView(stage);
  }

  function onPointerEnd(event) {
    if (!view.pointers.has(event.pointerId)) return;
    view.pointers.delete(event.pointerId);
    const gesture = view.gesture;
    const stage = zoomStage(view.img);
    if (!gesture || !stage) return;
    if (gesture.type === 'pinch') {
      if (view.pointers.size >= 2) {
        startPinch(stage); /* a third finger lifted: continue with the two that remain */
        return;
      }
      settlePinch(stage);
      if (view.pointers.size === 1) {
        const [id, point] = Array.from(view.pointers.entries())[0];
        startDrag(id, point, true);
        return;
      }
      view.gesture = null;
      stage.classList.remove('is-dragging');
      return;
    }
    if (event.pointerId !== gesture.id) return;
    view.gesture = null;
    stage.classList.remove('is-dragging');
    const dialog = stage.closest('dialog');
    if (event.type === 'pointerup' && !gesture.moved) {
      toggleZoom(stage, event.clientX, event.clientY);
      return;
    }
    const dx = event.clientX - gesture.sx;
    const dy = event.clientY - gesture.sy;
    const swipe = event.type === 'pointerup' && view.scale <= 1 && !gesture.pinched && event.pointerType !== 'mouse' &&
      Math.abs(dx) >= SWIPE_MIN && Math.abs(dx) > Math.abs(dy) * 1.5;
    if (swipe && zoomSlides(dialog).length > 1) stepSlide(dialog, dx < 0 ? 1 : -1);
    else applyView(stage); /* snap back after a short swipe */
  }

  document.addEventListener('pointerdown', onPointerDown);
  document.addEventListener('pointermove', onPointerMove);
  document.addEventListener('pointerup', onPointerEnd);
  document.addEventListener('pointercancel', onPointerEnd);
  document.addEventListener('dragstart', (event) => {
    if (event.target instanceof Element && event.target.closest('[data-zoom-stage]')) event.preventDefault();
  });

  document.addEventListener('keydown', (event) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    const dialog = zoomDialog();
    if (!dialog || !dialog.open) return;
    if (event.key === 'Tab') {
      /* keep Tab inside the viewer (a native modal would let it leave for the browser UI after the last control) */
      const items = $$('button, [tabindex="0"]', dialog).filter((el) => !el.hidden && !el.disabled && el.getClientRects().length);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!dialog.contains(active) || (event.shiftKey ? active === first : active === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      if (zoomSlides(dialog).length < 2) return;
      event.preventDefault();
      stepSlide(dialog, event.key === 'ArrowRight' ? 1 : -1);
      return;
    }
    const target = event.target instanceof Element ? event.target : null;
    if ((event.key === 'Enter' || event.key === ' ') && target && target.matches('[data-zoom-stage]')) {
      event.preventDefault();
      toggleZoom(target);
    }
  });

  /* Escape, close button, backdrop, editor deselect: every close path resets and returns focus to the opener. */
  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || dialog.id !== 'zoom-dialog') return;
      resetView(null);
      $$('[data-zoom-stage]', dialog).forEach((stage) => {
        stage.classList.remove('is-zoomed', 'is-dragging');
        stage.setAttribute('aria-pressed', 'false');
      });
      const trigger = view.trigger && view.trigger.isConnected ? view.trigger : $('[data-zoom]');
      view.trigger = null;
      if (trigger && typeof trigger.focus === 'function') trigger.focus({ preventScroll: true });
    },
    true
  );

  /* A late image load or a resize changes the picture box: keep a magnified picture inside the stage. */
  const reclamp = () => {
    const stage = zoomStage(view.img);
    if (!stage || view.scale <= 1) return;
    clampView(stage);
    applyView(stage);
  };
  document.addEventListener(
    'load',
    (event) => {
      if (event.target === view.img) reclamp();
    },
    true
  );
  window.addEventListener('resize', reclamp);

  document.addEventListener('pointerover', (event) => {
    if (event.target instanceof Element && event.target.closest('[data-zoom]')) preloadZoom();
  });
  document.addEventListener('focusin', (event) => {
    if (event.target instanceof Element && event.target.closest('[data-zoom]')) preloadZoom();
  });

  /* ---- variant / size --------------------------------------------------------------------------------- */
  /* <s class="freddy-compare"> / <span class="freddy-unit-price">: visually hidden label + price text */
  function setLabelledPrice(el, label, value) {
    el.textContent = '';
    if (label) {
      const hidden = document.createElement('span');
      hidden.className = 'freddy-visually-hidden';
      hidden.textContent = label + ' ';
      el.append(hidden);
    }
    el.append(document.createTextNode(value));
  }

  function renderPrice(variant) {
    const data = state.data;
    const price = $('#variant-price');
    if (!price) return;
    if (variant.price) price.textContent = text(variant.price);
    const box = price.closest('.pdp-price');
    if (!box) return;
    if (data.show_compare_at) {
      let compare = $('.freddy-compare', box);
      if (variant.compare_at) {
        if (!compare) {
          compare = document.createElement('s');
          compare.className = 'freddy-compare';
          price.after(compare);
        }
        setLabelledPrice(compare, str('regular_price'), text(variant.compare_at));
      } else if (compare) {
        compare.remove();
      }
    }
    const badge = $('.freddy-sale-badge', box);
    if (badge) {
      badge.textContent = variant.sale_badge ? text(variant.sale_badge) : '';
      badge.hidden = !variant.sale_badge;
    }
    if (data.show_unit_price) {
      let unit = $('.freddy-unit-price', box);
      if (variant.unit_price) {
        if (!unit) {
          unit = document.createElement('span');
          unit.className = 'freddy-unit-price';
          box.append(unit);
        }
        setLabelledPrice(unit, str('unit_price_label'), text(variant.unit_price));
      } else if (unit) {
        unit.remove();
      }
    }
  }

  function quantityValue() {
    const input = $('#quantity');
    if (!input) return 1;
    const qty = Number(input.value);
    return Number.isInteger(qty) && qty >= 1 && qty <= maxQuantity() ? qty : 1;
  }

  function updateBuyNow() {
    const link = $('#buy-original');
    const data = state.data;
    if (!link || !data || !state.selected) return;
    link.href = (data.cart_url || '/cart') + '/' + state.selected.id + ':' + quantityValue();
  }

  function setFeedback(message, isError) {
    const feedback = $('#size-feedback');
    if (!feedback) return;
    feedback.textContent = message;
    feedback.classList.toggle('error', Boolean(isError));
  }

  function selectedMessage(variant) {
    return str('selected', 'sizeSelected').split('[size]').join(variant.size);
  }

  function selectSize(button) {
    if (!load() || button.disabled) return;
    const variant = variantById(button.dataset.variant);
    if (!variant) return;
    state.selected = variant;
    $$('.size-button').forEach((other) => other.setAttribute('aria-pressed', String(other === button)));
    renderPrice(variant);
    setFeedback(selectedMessage(variant), false);
    const mobile = $('#mobile-add');
    if (mobile && mobile.getAttribute('aria-disabled') !== 'true') {
      const label = str('mobile_selected', 'addToBag');
      if (label) mobile.textContent = label;
    }
    updateBuyNow();
    document.dispatchEvent(
      new CustomEvent('freddy:variant:change', {
        detail: {
          sectionId: state.data.section_id,
          variantId: variant.id,
          size: variant.size,
          available: Boolean(variant.available),
        },
      })
    );
  }

  function chooseFirst() {
    setFeedback(str('choose_first', 'sizeRequired'), true);
    const buttons = $('.size-buttons');
    if (buttons) buttons.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const first = $('.size-button:not(:disabled)');
    if (first) first.focus({ preventScroll: true });
  }

  /* ---- add to bag ------------------------------------------------------------------------------------- */
  function cartAdd(items) {
    if (F.cart && typeof F.cart.add === 'function') return F.cart.add(items, { source: 'product' });
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = ((F.routes && F.routes.cartAdd) || root + 'cart/add') + '.js';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    }).then(async (response) => {
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(json.description || json.message || str('error', 'cartError'));
        error.status = response.status;
        throw error;
      }
      window.location.href = (state.data && state.data.cart_url) || '/cart';
      return json;
    });
  }

  function setBusy(isBusy) {
    ['#add-to-bag', '#mobile-add'].forEach((selector) => {
      const button = $(selector);
      if (!button) return;
      if (isBusy) {
        button.dataset.freddyLabel = button.textContent;
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
        if (selector === '#add-to-bag') {
          const adding = str('adding', 'adding');
          if (adding) button.textContent = adding;
        }
      } else {
        if (button.dataset.freddyLabel !== undefined) {
          button.textContent = button.dataset.freddyLabel;
          delete button.dataset.freddyLabel;
        }
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    });
  }

  async function add() {
    const data = load();
    if (!data || state.busy) return;
    const addButton = $('#add-to-bag');
    if (data.sold_out || (addButton && addButton.getAttribute('aria-disabled') === 'true')) return;
    if (!state.selected) {
      chooseFirst();
      return;
    }
    const input = $('#quantity');
    let quantity = 1;
    if (input) {
      if (!input.reportValidity()) return;
      quantity = Number(input.value);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > maxQuantity()) return;
    }
    if (!state.selected.available) {
      setFeedback(str('sold_out', 'soldOut'), true);
      return;
    }
    const variant = state.selected;
    state.busy = true;
    setBusy(true);
    try {
      await cartAdd([{ id: Number(variant.id), quantity }]);
      const feedback = $('#size-feedback');
      if (feedback && feedback.classList.contains('error') && $('.size-button')) setFeedback(selectedMessage(variant), false);
    } catch (error) {
      setFeedback((error && error.message) || str('error', 'cartError'), true);
    } finally {
      state.busy = false;
      setBusy(false);
    }
  }

  /* ---- related products: Shopify recommendations (source = recommendations) -------------------------- */
  function loadRecommendations(root = document) {
    $$('script[data-freddy-recommendations]', root).forEach(async (script) => {
      if (script.dataset.freddyLoaded) return;
      script.dataset.freddyLoaded = 'true';
      let config;
      try {
        config = JSON.parse(script.textContent);
      } catch (_) {
        return;
      }
      if (!config || !config.url) return;
      try {
        const response = await fetch(config.url);
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const freshGrid = doc.querySelector('.related-grid');
        if (!freshGrid || !freshGrid.children.length) return;
        const wrapper = script.closest('.shopify-section') || script.parentElement;
        const currentGrid = wrapper ? $('.related-grid', wrapper) : null;
        if (currentGrid) {
          currentGrid.innerHTML = freshGrid.innerHTML;
        } else {
          const freshSection = freshGrid.closest('section');
          if (freshSection) script.before(document.importNode(freshSection, true));
        }
      } catch (_) {
        /* keep the server-rendered fallback */
      }
    });
  }

  /* ---- delegated listeners ---------------------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const thumb = target.closest('.product-thumbs button');
    if (thumb) {
      selectThumb(thumb);
      return;
    }
    const galleryStep = target.closest('[data-gallery-prev], [data-gallery-next]');
    if (galleryStep) {
      stepThumb(galleryStep.matches('[data-gallery-next]') ? 1 : -1);
      return;
    }
    const zoomTrigger = target.closest('[data-zoom]');
    if (zoomTrigger) {
      if (Date.now() < swipeUntil) return;
      openZoom(zoomTrigger);
      return;
    }
    const zoomStep = target.closest('[data-zoom-prev], [data-zoom-next]');
    if (zoomStep) {
      const dialog = zoomStep.closest('dialog');
      if (dialog) stepSlide(dialog, zoomStep.matches('[data-zoom-next]') ? 1 : -1);
      return;
    }
    if (target.closest('.size-guide-open')) {
      show('#size-dialog');
      return;
    }
    const size = target.closest('.size-button');
    if (size) {
      selectSize(size);
      return;
    }
    if (target.closest('#add-to-bag') || target.closest('#mobile-add')) {
      add();
      return;
    }
    const buyNow = target.closest('#buy-original');
    if (buyNow) {
      const data = load();
      if (!data) return;
      if (data.sold_out) {
        event.preventDefault();
        return;
      }
      if (!state.selected) {
        event.preventDefault();
        chooseFirst();
        return;
      }
      const input = $('#quantity');
      if (input && !input.reportValidity()) {
        event.preventDefault();
        return;
      }
      updateBuyNow();
    }
  });

  document.addEventListener('input', (event) => {
    if (event.target && event.target.id === 'quantity' && load()) updateBuyNow();
  });

  /* A cart request finished elsewhere (e.g. drawer): never leave the PDP buttons stuck. */
  document.addEventListener('freddy:cart:updated', () => {
    if (!state.busy && $('#add-to-bag[aria-busy="true"]')) setBusy(false);
  });

  /* ---- theme editor ----------------------------------------------------------------------------------- */
  const hasSizeDialog = (section) => Boolean(section && section.querySelector && section.querySelector('#size-dialog'));
  let dialogsSelected = false;

  document.addEventListener('shopify:section:load', (event) => {
    load();
    loadRecommendations(event.target || document);
    /* a settings change re-renders the dialogs section: reopen the size dialog while it is being edited */
    if (dialogsSelected && hasSizeDialog(event.target)) show('#size-dialog');
  });
  document.addEventListener('shopify:section:select', (event) => {
    if (!hasSizeDialog(event.target)) return;
    dialogsSelected = true;
    show('#size-dialog');
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (!hasSizeDialog(event.target)) return;
    dialogsSelected = false;
    close('#size-dialog');
    close('#zoom-dialog');
  });
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    if (block && block.closest && block.closest('#size-dialog')) show('#size-dialog');
  });

  load();
  loadRecommendations();

  F.product = Object.assign(F.product || {}, {
    refresh: () => {
      state.el = null;
      return load();
    },
    selected: () => state.selected,
  });
})();
